import { useEffect, useState } from "react";
import {
  confirmGuestTabletData,
  resetGuestTabletSessionPublic,
  selectGuestTabletPitch,
} from "@/lib/guestTabletApi";
import type { GuestTabletSessionDisplay } from "@/types/guestTablet";
import { TabletGuestDataReview } from "./TabletGuestDataReview";
import { TabletGuestGuide } from "./TabletGuestGuide";
import { TabletInvoiceDisplay } from "./TabletInvoiceDisplay";
import { TabletPitchPlan } from "./TabletPitchPlan";
import { TabletStepNav } from "./TabletStepNav";
import { TabletWelcomeScreen } from "./TabletWelcomeScreen";

const SUCCESS_IDLE_MS = 30_000;

type Props = {
  data: GuestTabletSessionDisplay;
  workstationCode: string;
  onSessionUpdate: (session: GuestTabletSessionDisplay) => void;
  onRefresh: () => void;
};

export function GuestTabletWizard({ data, workstationCode, onSessionUpdate, onRefresh }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [selectingPitch, setSelectingPitch] = useState(false);
  const step = data.guest_display_step ?? "welcome";
  const guestDataConfirmed = data.pitch_selection?.guest_data_confirmed ?? false;
  const skipReviewConfirmation = Boolean(data.pitch_selection?.skip_review_confirmation);

  useEffect(() => {
    if (step !== "complete") return;
    const timer = window.setTimeout(() => {
      void resetGuestTabletSessionPublic(workstationCode).then((session) => onSessionUpdate(session));
    }, SUCCESS_IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [step, workstationCode, onSessionUpdate]);

  async function handleConfirmData() {
    if (step !== "review" || confirming || guestDataConfirmed) return;
    setConfirming(true);
    try {
      const session = await confirmGuestTabletData(workstationCode);
      onSessionUpdate(session);
    } catch {
      onRefresh();
    } finally {
      setConfirming(false);
    }
  }

  async function handlePitchSelect(pitchId: string) {
    if (selectingPitch) return;
    setSelectingPitch(true);
    try {
      const session = await selectGuestTabletPitch(workstationCode, pitchId);
      onSessionUpdate(session);
    } catch {
      onRefresh();
    } finally {
      setSelectingPitch(false);
    }
  }

  if (!data.welcome) return null;

  return (
    <div className="guest-tablet-wizard flex min-h-0 flex-1 flex-col gap-4">
      {step !== "complete" && step !== "pitch_choice" && step !== "welcome" ? (
        <TabletStepNav currentStep={step} />
      ) : null}

      {step === "welcome" ? (
        <div className="guest-tablet-step-panel flex min-h-0 flex-1 flex-col">
          <TabletWelcomeScreen welcome={data.welcome} isDemo={data.is_demo_mode} />
        </div>
      ) : null}

      {step === "pitch_choice" && data.site_plan ? (
        <div className="guest-tablet-step-panel flex min-h-0 flex-1 flex-col gap-4">
          <div className="guest-tablet-card min-h-0 flex-1 overflow-y-auto p-6 md:p-8">
            <h2 className="text-2xl font-bold text-emerald-950 dark:text-emerald-50">
              Wählen Sie Ihren Stellplatz
            </h2>
            <p className="mt-2 text-lg text-emerald-900/80 dark:text-emerald-50/80">
              Bitte tippen Sie einen der hervorgehobenen Plätze an.
            </p>
            <div className="mt-6">
              <TabletPitchPlan
                sitePlan={data.site_plan}
                selecting
                onPitchSelect={(id) => void handlePitchSelect(id)}
              />
            </div>
            {selectingPitch ? (
              <p className="mt-4 text-center text-lg text-emerald-800">Wird übernommen…</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === "review" ? (
        <div className="guest-tablet-step-panel flex min-h-0 flex-1 flex-col gap-4">
          <div className="guest-tablet-card min-h-0 flex-1 overflow-y-auto p-6 md:p-8">
            <h2 className="text-2xl font-bold text-emerald-950 dark:text-emerald-50">
              Ihre Daten zur Kontrolle
            </h2>
            <p className="mt-2 text-lg text-emerald-900/80 dark:text-emerald-50/80">
              Bitte prüfen Sie Ihre persönlichen Angaben und die Daten Ihrer Mitreisenden.
            </p>
            <div className="mt-6">
              <TabletGuestDataReview welcome={data.welcome} />
            </div>
            {skipReviewConfirmation ? (
              <p className="mt-6 rounded-xl bg-emerald-700/15 px-4 py-3 text-lg font-medium text-emerald-900 dark:text-emerald-50">
                Die Rezeption schließt den Check-in ab. Sie müssen hier nichts bestätigen.
              </p>
            ) : guestDataConfirmed ? (
              <p className="mt-6 rounded-xl bg-emerald-700/15 px-4 py-3 text-lg font-medium text-emerald-900 dark:text-emerald-50">
                Vielen Dank – Ihre Daten wurden bestätigt. Die Rezeption schließt den Check-in ab.
              </p>
            ) : (
              <p className="mt-6 text-lg text-emerald-900/80 dark:text-emerald-50/80">
                Wenn alles stimmt, bestätigen Sie mit dem Button unten.
              </p>
            )}
          </div>
          {!guestDataConfirmed && !skipReviewConfirmation ? (
            <button
              type="button"
              className="guest-tablet-touch-btn w-full bg-emerald-800 text-white shadow-lg disabled:opacity-60"
              disabled={confirming}
              onClick={() => void handleConfirmData()}
            >
              {confirming ? "Wird übernommen…" : "Meine Daten sind korrekt"}
            </button>
          ) : null}
        </div>
      ) : null}

      {step === "guest_guide" ? (
        <div className="guest-tablet-step-panel flex min-h-0 flex-1 flex-col gap-3">
          <p className="rounded-xl bg-emerald-800/10 px-4 py-3 text-center text-lg font-medium text-emerald-900 dark:bg-white/10 dark:text-emerald-50">
            Ihre Rechnung wird erstellt – in der Zwischenzeit finden Sie hier Ihre digitale
            Gästemappe.
          </p>
          <div className="min-h-0 flex-1 overflow-hidden">
            <TabletGuestGuide sections={data.guest_guide} wizardMode />
          </div>
        </div>
      ) : null}

      {step === "invoice" && data.invoice ? (
        <TabletInvoiceDisplay
          invoice={data.invoice}
          pendingPayment={data.pending_payment}
          workstationCode={workstationCode}
          onSessionUpdate={onSessionUpdate}
          onRefresh={onRefresh}
        />
      ) : null}

      {step === "invoice" && !data.invoice ? (
        <div className="guest-tablet-card guest-tablet-step-panel flex flex-1 flex-col items-center justify-center p-8 text-center">
          <p className="text-xl font-semibold">Rechnung wird vorbereitet…</p>
          <p className="mt-2 text-lg text-emerald-900/70 dark:text-emerald-50/70">
            Bitte einen Moment Geduld – Sie werden gleich zur Zahlung aufgefordert.
          </p>
        </div>
      ) : null}

      {step === "complete" ? (
        <div className="guest-tablet-card guest-tablet-step-panel flex flex-1 flex-col items-center justify-center p-8 text-center">
          <p className="text-3xl font-bold text-emerald-800 dark:text-emerald-200">
            Zahlung erfolgreich
          </p>
          <p className="mt-4 max-w-lg text-xl text-emerald-900/80 dark:text-emerald-50/80">
            Willkommen, {data.welcome.guest_display_name}! Wir wünschen Ihnen einen angenehmen
            Aufenthalt.
          </p>
          <p className="mt-6 text-base text-emerald-800/60 dark:text-emerald-100/60">
            Diese Meldung schließt sich in Kürze automatisch.
          </p>
        </div>
      ) : null}
    </div>
  );
}
